import { useState, useEffect, useRef } from "react";

/**
 * Mounts its children only when scrolled into (or near) the viewport.
 *
 * Use this to wrap heavy below-the-fold charts so the Stock Analysis page's
 * first paint isn't strangled by 10+ simultaneous network requests. Once the
 * placeholder enters the rootMargin band (default 400px before reaching the
 * viewport), the real children mount and start fetching their data.
 *
 * Props:
 *   minHeight  — placeholder height while not yet visible (prevents layout
 *                jump when the real component mounts). Default 350px.
 *   rootMargin — how far below the viewport to start mounting. Default 400px.
 *   className  — placeholder wrapper className.
 *   children   — the heavy component(s) to render once visible.
 */
export default function LazyOnScroll({
  minHeight = 350,
  rootMargin = "400px",
  className = "",
  children,
}) {
  const [shouldRender, setShouldRender] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (shouldRender) return;
    if (typeof IntersectionObserver === "undefined") {
      // Safety: fall back to immediate render if the browser doesn't support it
      setShouldRender(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldRender(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin, threshold: 0 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [shouldRender, rootMargin]);

  if (shouldRender) return <>{children}</>;

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight }}
      data-testid="lazy-on-scroll-placeholder"
      aria-busy="true"
    >
      <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
        ↓ scroll to load
      </div>
    </div>
  );
}
